import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { MapPin, Loader2 } from 'lucide-react';

interface LocationData {
  address: string;
  latitude?: number;
  longitude?: number;
}

interface LocationInputProps {
  value?: string;
  onChange: (location: LocationData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface MapboxFeature {
  place_name: string;
  center: [number, number];
  geometry: {
    coordinates: [number, number];
  };
}

export function LocationInput({ 
  value = '', 
  onChange, 
  placeholder = "Entrez une adresse...", 
  className,
  disabled 
}: LocationInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Récupérer le token Mapbox (token public - OK de le mettre directement dans le code)
  // Le token a été configuré dans les secrets Supabase mais pour le frontend, on utilise directement le token public
  const MAPBOX_TOKEN = 'pk.eyJ1IjoibG92YWJsZSIsImEiOiJjbTVhbmlxZ2MwMHp5MnBzZGZrejRsbTc1In0.7X8rJ9mhlDKEVYt-iHqp5Q';

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const searchLocations = async (query: string) => {
    if (!query.trim() || query.length < 2 || !MAPBOX_TOKEN) {
      // Fallback si pas de token Mapbox
      if (!query.trim() || query.length < 2) return;
      return await searchWithOSM(query);
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=FR&types=place,locality,region,postcode,address,poi&autocomplete=true&proximity=ip&limit=8&language=fr`
      );
      
      if (response.ok) {
        const data = await response.json();
        const features = data.features || [];
        if (features.length > 0) {
          setSuggestions(features);
          return;
        }
        // Si Mapbox répond mais sans résultats, tenter OSM
        await searchWithOSM(query);
      } else {
        console.warn('Mapbox geocoding non OK:', response.status, response.statusText);
        await searchWithOSM(query);
      }
    } catch (error) {
      console.error("Erreur Mapbox:", error);
      await searchWithOSM(query);
    } finally {
      setIsLoading(false);
    }
  };

  const searchWithOSM = async (query: string) => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=0&limit=8&accept-language=fr`;
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
        },
      });
      if (!res.ok) {
        console.warn('OSM geocoding non OK:', res.status, res.statusText);
        setSuggestions([]);
        return;
      }
      const items: Array<{ display_name: string; lat: string; lon: string; }> = await res.json();
      const transformed = items.map((it) => ({
        place_name: it.display_name,
        center: [parseFloat(it.lon), parseFloat(it.lat)] as [number, number],
        geometry: { coordinates: [parseFloat(it.lon), parseFloat(it.lat)] as [number, number] },
      }));
      setSuggestions(transformed as unknown as MapboxFeature[]);
    } catch (e) {
      console.error('Erreur OSM:', e);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Si l'utilisateur tape, on réinitialise les coordonnées
    onChange({
      address: newValue,
      latitude: undefined,
      longitude: undefined
    });

    // Debounce pour éviter trop de requêtes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (newValue.length >= 2) {
        setShowSuggestions(true);
        searchLocations(newValue);
      } else {
        setShowSuggestions(false);
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSuggestionClick = (feature: MapboxFeature) => {
    const [longitude, latitude] = feature.center;
    setInputValue(feature.place_name);
    setShowSuggestions(false);
    setSuggestions([]);
    
    onChange({
      address: feature.place_name,
      latitude,
      longitude
    });
  };

  const handleBlur = () => {
    // Délai pour permettre le clic sur une suggestion
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Si pas de token Mapbox, afficher un input simple
  if (!MAPBOX_TOKEN) {
    return (
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange({ address: e.target.value });
          }}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
        />
        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className={cn("pr-10", className)}
          disabled={disabled}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <MapPin className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>
      
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.length > 0 ? (
            suggestions.map((feature, index) => (
              <button
                key={index}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground flex items-center gap-2 transition-colors"
                onClick={() => handleSuggestionClick(feature)}
              >
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{feature.place_name}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">Aucun résultat</div>
          )}
        </div>
      )}
    </div>
  );
}
